import type { AttuneMcpToolName } from '@/lib/attune/mcp-tools';
import type { ToolId } from './tools';

// Keep this module browser-safe: runtime tool declarations include server dependencies.
const ACTION_PROGRESS = {
  Activate: 'Activating',
  Add: 'Adding',
  Apply: 'Applying',
  Archive: 'Archiving',
  Ask: 'Asking',
  Assign: 'Assigning',
  Build: 'Building',
  Calculate: 'Calculating',
  Check: 'Checking',
  Claim: 'Claiming',
  Compare: 'Comparing',
  Continue: 'Continuing',
  Create: 'Creating',
  Draft: 'Drafting',
  Edit: 'Editing',
  Evaluate: 'Evaluating',
  Export: 'Exporting',
  Find: 'Finding',
  Finish: 'Finishing',
  Generate: 'Generating',
  Load: 'Loading',
  Link: 'Linking',
  Map: 'Mapping',
  Pause: 'Pausing',
  Preview: 'Previewing',
  Publish: 'Publishing',
  Read: 'Reading',
  Record: 'Recording',
  Refresh: 'Refreshing',
  Research: 'Researching',
  Request: 'Requesting',
  Restore: 'Restoring',
  Resume: 'Resuming',
  Review: 'Reviewing',
  Revise: 'Revising',
  Run: 'Running',
  Save: 'Saving',
  Schedule: 'Scheduling',
  Search: 'Searching',
  Start: 'Starting',
  Submit: 'Submitting',
  Update: 'Updating',
  Use: 'Using',
} as const;

type ToolCopy = readonly [action: `${keyof typeof ACTION_PROGRESS} ${string}`, subject: string];

// Explicit copy for every built-in action. Type checking catches new catalog entries
// without a label; unknown and connected-app actions still have readable fallbacks.
export const TOOL_COPY = {
  'workspace.modules': ['Load workspace modules', 'Workspace modules'],
  'intelligence.prepare_setup': ['Draft Intelligence setup', 'Intelligence draft'],
  'intelligence.confirm_setup': ['Start Intelligence collection', 'Intelligence collection'],
  'intelligence.status': ['Check Intelligence status', 'Intelligence status'],
  'intelligence.retry': ['Start Intelligence retry', 'Intelligence collection'],
  'workspace.guide': ['Read workspace guidance', 'Workspace guidance'],
  'product_updates.list': ['Load product updates', 'Product updates'],
  'product_updates.prepare_create': ['Draft a product update', 'Product update draft'],
  'product_updates.confirm_create': ['Publish a product update', 'Product update'],
  'workspaces.list': ['Load your workspaces', 'Your workspaces'],
  'projects.prepare_create': ['Review project setup', 'Project setup'],
  'projects.create': ['Create your project', 'Project setup'],
  'projects.status': ['Check setup progress', 'Setup progress'],
  'projects.resume': ['Resume project setup', 'Project setup'],
  'projects.list': ['Load your projects', 'Your projects'],
  'notebook.session_create': ['Start a conversation', 'New conversation'],
  'notebook.session_use_default': ['Load your conversation', 'Your conversation'],
  'notebook.ask': ['Research your question', 'Question research'],
  'notebook.get_session': ['Read conversation history', 'Conversation history'],

  'skills.list': ['Find available skills', 'Available skills'],
  'skills.detail': ['Read skill instructions', 'Skill instructions'],
  'skills.validate': ['Check skill instructions', 'Skill review'],
  'skills.prepare_create': ['Draft a new skill', 'Skill draft'],
  'skills.confirm_create': ['Save a new skill', 'New skill'],
  'skills.prepare_update': ['Draft skill changes', 'Skill changes'],
  'skills.confirm_update': ['Save skill changes', 'Skill changes'],
  'skills.search': ['Find relevant skills', 'Skill discovery'],
  'skills.read': ['Read skill instructions', 'Skill instructions'],
  'skills.read_reference': ['Read skill reference material', 'Skill references'],

  'visibility.summary': ['Check brand visibility', 'Brand visibility'],
  'visibility.detail': ['Read visibility details', 'Visibility details'],
  'rank.summary': ['Check AI search rankings', 'AI search rankings'],
  'rank.detail': ['Read ranking details', 'Ranking details'],
  'brands.leaderboard': ['Compare brand rankings', 'Brand rankings'],
  'brands.prepare_group_update': ['Review competitor group changes', 'Competitor group changes'],
  'brands.update_group': ['Save competitor groups', 'Competitor groups'],
  'brands.prepare_variations_update': ['Review brand name variations', 'Brand name variations'],
  'brands.update_variations': ['Save brand name variations', 'Brand name variations'],
  'sentiment.summary': ['Check brand sentiment', 'Brand sentiment'],
  'answers.evidence.summary': ['Read platform results', 'Platform results'],
  'answers.evidence': ['Read additional evidence', 'Additional evidence'],
  'answers.evidence.search': ['Search additional evidence', 'Evidence search'],
  'answers.read': ['Read AI answers', 'AI answers'],
  'citations.summary': ['Review citation trends', 'Citation trends'],
  'citations.rows': ['Find cited pages', 'Cited pages'],
  'citations.content': ['Read cited content', 'Cited content'],
  'publishers.overview': ['Read publisher performance', 'Publisher report'],
  'publishers.pages': ['Read publisher citation pages', 'Publisher pages'],
  'channel_manager.summary': ['Review outreach opportunities', 'Outreach opportunities'],
  'channel_manager.detail': ['Read outreach details', 'Outreach details'],
  'channel_manager.update_workflow': ['Update outreach progress', 'Outreach progress'],
  'channel_manager.add_note': ['Save an outreach note', 'Outreach note'],
  'channel_manager.trigger_reenrichment': ['Refresh outreach research', 'Outreach research'],
  'channel_manager.get_best_contact_route': ['Find contact options', 'Contact options'],
  'channel_manager.compose_outreach_template': ['Draft an outreach message', 'Outreach draft'],
  'prompts.tracking': ['Load tracked prompts', 'Tracked prompts'],
  'prompts.prepare_update': ['Review prompt changes', 'Prompt changes'],
  'prompts.confirm_update': ['Save prompt changes', 'Prompt changes'],
  'prompts.prepare_group_update': ['Review prompt group changes', 'Prompt group changes'],
  'prompts.update_group': ['Save prompt groups', 'Prompt groups'],
  'prompts.summary': ['Review prompt performance', 'Prompt performance'],
  'prompts.detail': ['Read prompt details', 'Prompt details'],
  'pages.summary': ['Review your website pages', 'Website pages'],
  'pages.search': ['Search your pages', 'Page search'],
  'pages.read': ['Read page content', 'Page content'],
  'page_review.start': ['Start a page review', 'Page review'],
  'page_review.get': ['Read a page review', 'Page review'],
  'page_review.list': ['Load page reviews', 'Page reviews'],
  'page_review.queue': ['Find pages to review', 'Pages to review'],
  'page_review.locate': ['Find supporting page text', 'Supporting page text'],
  'page_review.submit': ['Save page recommendations', 'Page recommendations'],
  'page_review.create_task': ['Create a page improvement task', 'Page improvement task'],
  'readiness.run': ['Check agent readiness', 'Agent readiness'],
  'website.map': ['Map website pages', 'Website map'],
  'website.performance': ['Check website performance', 'Website performance'],

  'analytics.sources': ['Check connected analytics', 'Connected analytics'],
  'analytics.pageMetrics': ['Check page performance', 'Page performance'],
  'analytics.queryMetrics': ['Check search performance', 'Search performance'],
  'analytics.channelMetrics': ['Compare traffic channels', 'Traffic channels'],
  'analytics.uxSignals': ['Review visitor behavior', 'Visitor behavior'],
  'analytics.aiCrawlers': ['Check AI crawler activity', 'AI crawler activity'],
  'analytics.summary': ['Review website analytics', 'Website analytics'],
  'analytics.pages': ['Check page performance', 'Page performance'],
  'analytics.search_queries': ['Check search performance', 'Search performance'],
  'analytics.channels': ['Compare traffic channels', 'Traffic channels'],
  'analytics.ux': ['Review visitor behavior', 'Visitor behavior'],
  'analytics.page_context': ['Review page insights', 'Page insights'],
  'ads.sources': ['Check connected ad accounts', 'Connected ad accounts'],
  'ads.summary': ['Review ad performance', 'Ad performance'],
  'gsc.urlInspection.inspect': ['Check Google indexing', 'Google indexing'],

  'north_star.get': ['Read your growth strategy', 'Growth strategy'],
  'north_star.prepare_update': ['Draft strategy changes', 'Strategy changes'],
  'north_star.confirm_update': ['Save your growth strategy', 'Growth strategy'],
  'agents.templates': ['Find background task templates', 'Background task templates'],
  'agents.prompts': ['Read background task instructions', 'Background task instructions'],
  'agents.prepare_create': ['Review background task setup', 'Background task setup'],
  'agents.confirm_create': ['Create a background task', 'Background task'],
  'agents.list': ['Load background tasks', 'Background tasks'],
  'agents.detail': ['Read background task details', 'Background task details'],
  'agents.pause': ['Pause a background task', 'Background task pause'],
  'agents.resume': ['Resume a background task', 'Background task resume'],
  'agents.update_model': ['Update the task model', 'Task model'],
  'agents.slack_destinations': ['Find Slack delivery channels', 'Slack delivery channels'],
  'agents.update_delivery': ['Update task Slack delivery', 'Task Slack delivery'],
  'agents.run_now': ['Start a background task', 'Background task run'],
  'scan_runs.list': ['Load background task history', 'Background task history'],
  'scan_runs.detail': ['Read background task results', 'Background task results'],
  'growth_program.get': ['Read your growth program', 'Growth program'],
  'growth_program.cockpit': ['Review growth progress', 'Growth progress'],
  'growth_program.prepare': ['Draft a growth program', 'Growth program draft'],
  'growth_program.revise_proposal': ['Revise the growth proposal', 'Growth proposal'],
  'growth_program.confirm': ['Activate your growth program', 'Growth program activation'],
  'growth_program.research_start': ['Start growth research', 'Growth research'],
  'growth_program.research_complete': ['Evaluate research findings', 'Research evaluation'],
  'growth_program.intervention_evaluate': ['Evaluate growth results', 'Growth evaluation'],
  'growth_program.policy_prepare_promotion': ['Review automation changes', 'Automation changes'],
  'growth_program.policy_confirm_promotion': ['Apply automation changes', 'Automation changes'],
  'growth_program.context': ['Review growth program context', 'Growth program context'],
  'growth_program.reconcile': ['Update growth priorities', 'Growth priorities'],

  'docs.list': ['Load your documents', 'Your documents'],
  'docs.get': ['Read a document', 'Document'],
  'docs.create': ['Create a document', 'New document'],
  'docs.update': ['Update a document', 'Document update'],
  'docs.apply_operations': ['Edit a document', 'Document edits'],
  'docs.versions': ['Load document history', 'Document history'],
  'docs.restore': ['Restore a document version', 'Document restoration'],
  'docs.schedule': ['Schedule document updates', 'Document schedule'],
  'docs.refresh': ['Refresh a document', 'Document refresh'],
  'docs.archive': ['Archive a document', 'Document archive'],
  'reports.list': ['Load your reports', 'Your reports'],
  'reports.get': ['Read a report', 'Report'],
  'reports.create': ['Create a report', 'New report'],
  'reports.update': ['Update a report', 'Report update'],
  'reports.apply_operations': ['Edit a report', 'Report edits'],
  'reports.versions': ['Load report history', 'Report history'],
  'reports.restore': ['Restore a report version', 'Report restoration'],
  'reports.schedule': ['Schedule report updates', 'Report schedule'],
  'reports.refresh': ['Refresh a report', 'Report refresh'],
  'reports.archive': ['Archive a report', 'Report archive'],
  'growth_inbox.create': ['Add a growth opportunity', 'Growth opportunity'],
  'growth_inbox.list': ['Review growth opportunities', 'Growth opportunities'],
  'growth_inbox.detail': ['Read opportunity details', 'Opportunity details'],
  'growth_inbox.claim': ['Claim a growth opportunity', 'Opportunity assignment'],
  'growth_inbox.add_work_log': ['Save a progress update', 'Progress update'],
  'growth_inbox.submit_receipt': ['Submit completed work', 'Work submission'],
  'growth_inbox.outcome': ['Record a growth outcome', 'Growth outcome'],
  'task.get': ['Read task details', 'Task details'],
  'task.create': ['Create a task', 'New task'],
  'task.update': ['Update a task', 'Task update'],
  'task.comment': ['Add a task comment', 'Task comment'],
  'task.dispatch': ['Start task work', 'Task work'],
  'task.follow_up': ['Continue task work', 'Task follow-up'],
  'task.change_request.create': ['Request task changes', 'Task change request'],
  'task.change_request.update': ['Update a change request', 'Task change request'],
  'objective.list': ['Load objectives', 'Objectives'],
  'objective.get': ['Read objective details', 'Objective details'],
  'objective.key_result.get': ['Check key result progress', 'Key result progress'],
  'objective.create': ['Create an objective', 'New objective'],
  'objective.key_result.check_in': ['Record key result progress', 'Key result check-in'],
  'objective.update': ['Update an objective', 'Objective update'],
  'objective.assign_task': ['Link a task to an objective', 'Objective task'],

  'webflow.sources': ['Check Webflow connections', 'Webflow connections'],
  'webflow.sites': ['Load Webflow sites', 'Webflow sites'],
  'webflow.collections': ['Load Webflow collections', 'Webflow collections'],
  'webflow.pageMappings': ['Find linked Webflow pages', 'Linked Webflow pages'],
  'webflow.pageCopy.inspect': ['Review Webflow page content', 'Webflow page content'],
  'webflow.draft.prepare': ['Draft Webflow changes', 'Webflow draft'],
  'webflow.draft.confirm': ['Save a Webflow draft', 'Webflow draft'],
  'webflow.publish.prepare': ['Review Webflow publishing changes', 'Webflow publishing review'],
  'webflow.publish.confirm': ['Publish Webflow changes', 'Webflow publishing'],
  'shopify.connections.list': ['Check Shopify connections', 'Shopify connections'],
  'shopify.permissions.status': ['Check Shopify access', 'Shopify access'],
  'shopify.products.search': ['Search Shopify products', 'Shopify products'],
  'shopify.products.read': ['Read Shopify product details', 'Shopify product details'],
  'shopify.content.search': ['Search Shopify content', 'Shopify content search'],
  'shopify.content.read': ['Read Shopify content', 'Shopify content'],
  'shopify.pageMappings.list': ['Find linked Shopify pages', 'Linked Shopify pages'],
  'shopify.metafields.definitions': [
    'Read Shopify custom field definitions',
    'Shopify custom fields',
  ],
  'shopify.metafields.read': ['Read Shopify custom fields', 'Shopify custom fields'],
  'shopify.analytics.query': ['Review Shopify analytics', 'Shopify analytics'],
  'shopify.orders.search': ['Search Shopify orders', 'Shopify orders'],
  'shopify.abandoned_checkouts.search': ['Search abandoned checkouts', 'Abandoned checkouts'],
  'shopify.descriptions.prepare_bulk_update': [
    'Draft product description changes',
    'Product description draft',
  ],
  'shopify.descriptions.preview_bulk_update': [
    'Preview product description changes',
    'Product description preview',
  ],
  'shopify.descriptions.confirm_bulk_update': ['Save product descriptions', 'Product descriptions'],
  'shopify.content.prepare_bulk_update': ['Draft Shopify content changes', 'Shopify content draft'],
  'shopify.content.confirm_bulk_update': [
    'Save Shopify content changes',
    'Shopify content changes',
  ],
  'shopify.content.prepare_article_create': ['Draft a Shopify article', 'Shopify article draft'],
  'shopify.content.confirm_article_create': ['Create a Shopify article', 'Shopify article'],
  'shopify.metafields.prepare_set': [
    'Review Shopify custom field changes',
    'Shopify custom field changes',
  ],
  'shopify.metafields.confirm_set': ['Save Shopify custom fields', 'Shopify custom fields'],
  'shopify.jobs.status': ['Check Shopify update progress', 'Shopify update progress'],
  'shopify.jobs.results': ['Read Shopify update results', 'Shopify update results'],
  'shopify.rollback.prepare': ['Review Shopify restoration', 'Shopify restoration review'],
  'shopify.rollback.confirm': ['Restore Shopify content', 'Shopify restoration'],
  'delivery.approvals.list': ['Load changes awaiting approval', 'Changes awaiting approval'],
  'delivery.approvals.archive': ['Archive delivery approval', 'Approval archived'],
  'delivery.approvals.read': ['Review changes for approval', 'Changes for approval'],
  'delivery.approvals.confirm': ['Apply approved changes', 'Approved changes'],
  'wordpress.connections.list': ['Check WordPress connections', 'WordPress connections'],
  'wordpress.sites.list': ['Load WordPress sites', 'WordPress sites'],
  'wordpress.permissions.status': ['Check WordPress access', 'WordPress access'],
  'wordpress.content.search': ['Search WordPress content', 'WordPress content search'],
  'wordpress.content.read': ['Read WordPress content', 'WordPress content'],
  'wordpress.pageMappings.list': ['Find linked WordPress pages', 'Linked WordPress pages'],
  'wordpress.editor.context': ['Review the WordPress editor', 'WordPress editor'],
  'wordpress.patterns.list': ['Find WordPress layouts', 'WordPress layouts'],
  'wordpress.patterns.read': ['Read a WordPress layout', 'WordPress layout'],
  'wordpress.analytics.summary': ['Review WordPress analytics', 'WordPress analytics'],
  'wordpress.draft.prepare': ['Draft WordPress changes', 'WordPress draft'],
  'wordpress.draft.confirm': ['Save a WordPress draft', 'WordPress draft'],
  'wordpress.publish.prepare': [
    'Review WordPress publishing changes',
    'WordPress publishing review',
  ],
  'wordpress.publish.confirm': ['Publish WordPress changes', 'WordPress publishing'],
  'wordpress.jobs.status': ['Check WordPress update progress', 'WordPress update progress'],
  'wordpress.jobs.results': ['Read WordPress update results', 'WordPress update results'],

  'github.sources': ['Check GitHub connections', 'GitHub connections'],
  'github.repository': ['Review the website repository', 'Website repository'],
  'github.content.search': ['Search website files', 'Website file search'],
  'github.content.read': ['Read website files', 'Website files'],
  'github.draft.prepare': ['Draft website changes', 'Website draft'],
  'github.jobs.status': ['Check website update progress', 'Website update progress'],
  'github.preview.start': ['Start a website preview', 'Website preview'],
  'github.preview.context': ['Review website preview details', 'Website preview details'],
  'github.preview.search': ['Search website preview files', 'Website preview search'],
  'github.preview.files.list': ['Load website preview files', 'Website preview files'],
  'github.preview.read': ['Read website preview files', 'Website preview files'],
  'github.preview.apply_patch': ['Edit the website preview', 'Website preview edits'],
  'github.preview.image.generate': ['Generate a website image', 'Website image'],
  'github.preview.asset.apply': ['Add an image to the website', 'Website image'],
  'github.preview.status': ['Check website preview progress', 'Website preview progress'],
  'github.preview.seo.inspect': ['Check website SEO', 'Website SEO inspection'],
  'github.preview.submit': ['Submit website changes for review', 'Website changes for review'],
  'astro.docs.search': ['Search Astro documentation', 'Astro documentation'],
  'canvas.list': ['Load your canvases', 'Your canvases'],
  'canvas.get': ['Load a canvas', 'Canvas'],
  'canvas.create': ['Create a canvas', 'New canvas'],
  'canvas.read': ['Read canvas files', 'Canvas files'],
  'canvas.search': ['Search canvas files', 'Canvas file search'],
  'canvas.apply_patch': ['Edit a canvas', 'Canvas edits'],
  'canvas.image.generate': ['Generate a canvas image', 'Canvas image'],
  'canvas.asset.add': ['Add a canvas asset', 'Canvas asset'],
  'canvas.render': ['Build the canvas preview', 'Canvas preview'],
  'canvas.export': ['Export a canvas', 'Canvas export'],
  'canvas.versions': ['Load canvas history', 'Canvas history'],
  'canvas.restore': ['Restore a canvas version', 'Canvas restoration'],
  'canvas.archive': ['Archive a canvas', 'Canvas archive'],
  'canvas.unarchive': ['Restore an archived canvas', 'Canvas restoration'],
  'design_system.get': ['Read brand design guidelines', 'Brand design guidelines'],
  'design_system.apply_patch': ['Update brand design guidelines', 'Design guideline changes'],
  'design_system.versions': ['Load design guideline history', 'Design guideline history'],
  'design_system.restore': ['Restore design guidelines', 'Design guideline restoration'],

  get_prompt_inventory: ['Load tracked prompts', 'Tracked prompts'],
  get_brand_citations: ['Find citations mentioning your brand', 'Citations mentioning your brand'],
  get_competitor_citations: ['Find citations without your brand', 'Citations without your brand'],
  get_top_citations: ['Find top cited sources', 'Top cited sources'],
  get_citation_content: ['Read cited content', 'Cited content'],
  search_citations: ['Search citations', 'Citation search'],
  search_answers: ['Search AI answers', 'AI answers'],
  web_search: ['Search the web', 'Web research'],
  onboarding_website_research: ['Research your website', 'Website research'],
  get_answers_by_platform: ['Read AI answers by platform', 'AI answers by platform'],
  search_user_pages: ['Search your pages', 'Page search'],
  get_user_page_details: ['Read page details', 'Page details'],
  get_session_transcript: ['Read conversation history', 'Conversation history'],
  get_brand_perception: ['Review brand perception', 'Brand perception'],
  get_brand_visibility: ['Check brand visibility', 'Brand visibility'],
  run_attune_data_tool: ['Continue your request', 'Requested action'],
  'tools.search': ['Find available actions', 'Available actions'],
  'tools.describe': ['Check action requirements', 'Action requirements'],
  'tools.call': ['Continue your request', 'Requested action'],
  'tools.call_read': ['Continue your request', 'Requested action'],
  'tools.call_write': ['Continue your request', 'Requested action'],
  'data.calculate': ['Calculate results', 'Calculations'],
  'data.calculate_batch': ['Calculate results', 'Calculations'],
  'evidence.read': ['Read supporting evidence', 'Supporting evidence'],
  record_growth_issue: ['Add a growth opportunity', 'Growth opportunity'],
  submit_background_findings: ['Save research findings', 'Research findings'],
  'growth_autonomy.research_submit': ['Submit research findings', 'Research findings'],
  'growth_autonomy.intervention_evaluation_submit': [
    'Submit growth evaluation',
    'Growth evaluation',
  ],
  'custom_mcp.tools.list': ['Check connected apps', 'Connected apps'],
  'plan.ask_user': ['Ask for your input', 'Your input'],
  'plan.submit': ['Submit a plan for review', 'Plan for review'],
  'prompts.groups': ['Load prompt topics', 'Prompt topics'],
  'topics.list': ['Load topics', 'Topics'],
  'topics.performance': ['Compare topic performance', 'Topic performance'],
  'prompts.prepare_group_delete': ['Review topic removal', 'Topic removal review'],
  'prompts.delete_group': ['Apply topic deletion', 'Topic removed'],
  'goal.complete': ['Finish the task', 'Task completion'],
  run_system_command: ['Run the requested workflow', 'Requested workflow'],
  slack_operator: ['Use Slack', 'Slack action'],
} as const satisfies Record<AttuneMcpToolName | ToolId, ToolCopy> & Record<string, ToolCopy>;

const toolCopy: Record<string, ToolCopy> = TOOL_COPY;
const FALLBACK_COPY: ToolCopy = ['Continue your request', 'Requested action'];
const CONNECTED_APP_COPY: ToolCopy = ['Use a connected app', 'Connected app'];

function copyForTool(toolName: string): ToolCopy {
  if (Object.hasOwn(toolCopy, toolName)) return toolCopy[toolName];
  return toolName.startsWith('custom_mcp.') ? CONNECTED_APP_COPY : FALLBACK_COPY;
}

export function resolveToolDisplayTarget(toolName: string, args?: Record<string, unknown>) {
  // Both current and saved conversations can wrap an action in a dispatch call.
  for (let depth = 0; depth < 4; depth += 1) {
    if (
      !['tools.call', 'tools.call_read', 'tools.call_write', 'run_attune_data_tool'].includes(
        toolName
      )
    )
      break;
    const selectedTool = toolName === 'run_attune_data_tool' ? args?.tool : args?.toolName;
    if (typeof selectedTool !== 'string' || !selectedTool.trim()) break;
    toolName = selectedTool.trim();
    const nested = args?.arguments;
    args =
      nested && typeof nested === 'object' && !Array.isArray(nested)
        ? (nested as Record<string, unknown>)
        : undefined;
  }
  return { toolName, args };
}

export function getToolProofLabel(toolName: string, args?: Record<string, unknown>): string {
  return copyForTool(resolveToolDisplayTarget(toolName, args).toolName)[1];
}

export function getToolActivityLabel(
  toolName: string,
  status: 'preparing' | 'calling' | 'done' | 'error',
  args?: Record<string, unknown>
): string {
  const [action, subject] = copyForTool(resolveToolDisplayTarget(toolName, args).toolName);
  if (status === 'done' || status === 'error') return subject;
  if (action === FALLBACK_COPY[0]) {
    return status === 'preparing' ? 'Preparing next step…' : 'Working on your request…';
  }
  if (status === 'preparing') {
    return `Preparing to ${action.charAt(0).toLowerCase()}${action.slice(1)}…`;
  }
  const space = action.indexOf(' ');
  const verb = action.slice(0, space) as keyof typeof ACTION_PROGRESS;
  return `${ACTION_PROGRESS[verb]}${action.slice(space)}…`;
}
